import type { Locale, Msg, Params } from './types.js';

/**
 * Единственный форматтер сообщений в проекте. Без зависимостей: его импортирует и сервер,
 * и клиентский бандл, и он обязан оставаться дешёвым.
 *
 * Поддерживает ровно два механизма — больше продукту не нужно, а ICU MessageFormat целиком
 * потянул бы за собой парсер:
 *   * подстановка `{name}`;
 *   * плюрали: ветка выбирается по Intl.PluralRules от параметра `p.by`, внутри ветки `#`
 *     заменяется на значение этого параметра.
 *
 * Неизвестный плейсхолдер оставляется как есть — молча пустая строка в интерфейсе хуже,
 * чем видимое `{name}`, по которому сразу понятно, что забыли передать параметр.
 */

const PLACEHOLDER = /\{(\w+)\}/g;

const PLURAL_RULES = new Map<string, Intl.PluralRules>();

function pluralRulesFor(locale: Locale): Intl.PluralRules {
  let rules = PLURAL_RULES.get(locale);
  if (rules === undefined) {
    rules = new Intl.PluralRules(locale);
    PLURAL_RULES.set(locale, rules);
  }
  return rules;
}

function substitute(template: string, params: Params): string {
  return template.replace(PLACEHOLDER, (whole, key: string) => {
    const value = params[key];
    return value === undefined ? whole : String(value);
  });
}

export function formatMsg<P extends Params>(m: Msg<P>, params: P, locale: Locale): string {
  if (m.p === undefined) return substitute(m.s, params);

  const count = params[m.p.by];
  if (typeof count !== 'number') {
    // Параметр плюрализации не передан или не число: единственное осмысленное поведение —
    // отдать базовую строку, а не выбрать ветку наугад.
    return substitute(m.s, params);
  }

  const category = pluralRulesFor(locale).select(count);
  const branch = m.p[category] ?? m.p.other ?? m.s;
  return substitute(branch.replace(/#/g, String(count)), params);
}
